import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

const ROUTE_LABELS = {
  '/': 'Home',
  '/meet-our-team': 'Meet Our Team',
  '/we-are-independent': 'Why Independent',
  '/insurance-companies': 'Insurance Carriers',
  '/learning-center': 'Learning Center',
  '/review-our-agency': 'Reviews',
  '/clickable-coverage': 'Clickable Coverage',
  '/blog': 'Blog',
  '/admin/blog': 'Admin Dashboard',
  '/admin/write': 'Write Post',
  '/resources': 'Resource Center',
  '/resources/calculator': 'Insurance Calculator',
  '/resources/checklist': 'Coverage Checklist',
  '/resources/explainers': 'Coverage Explainers',
  '/resources/market-reports': 'Local Market Reports',
  '/resources/va-escrow-calculator': 'VA Loan Escrow Calculator',
  '/resources/risk-score': 'Coverage Gap Risk Score',
  '/resources/checklists-download': 'PDF Checklists',
  '/services/home-insurance': 'Home Insurance',
  '/services/auto-insurance': 'Auto Insurance',
  '/services/business-insurance': 'Business Insurance',
  '/services/general-liability': 'General Liability',
  '/services/workers-comp': 'Workers\' Compensation',
  '/services/landlord-insurance': 'Landlord Insurance',
  '/services/rv-insurance': 'RV Insurance',
  '/loan-guides/va-loan': 'VA Loan Guide',
  '/loan-guides/fha-loan': 'FHA Loan Guide',
  '/loan-guides/conventional-loan': 'Conventional Loan Guide',
  '/loan-guides/usda-loan': 'USDA Loan Guide',
  '/locations/north-augusta-sc': 'North Augusta, SC',
  '/locations/aiken-sc': 'Aiken, SC',
  '/locations/augusta-ga': 'Augusta, GA',
  '/locations/evans-ga': 'Evans, GA',
  '/locations/martinez-ga': 'Martinez, GA',
  '/locations/grovetown-ga': 'Grovetown, GA',
  '/locations/columbia-sc': 'Columbia, SC',
  '/locations/greenville-sc': 'Greenville, SC',
  '/locations/charleston-sc': 'Charleston, SC',
  '/locations/atlanta-ga': 'Atlanta, GA',
  '/find-my-coverage': 'Find My Coverage',
  '/get-a-quote': 'Get a Quote',
  '/faq': 'FAQ',
  '/resources/savings-calculator': 'Savings Calculator',
  '/privacy-policy': 'Privacy Policy',
  '/terms': 'Terms & Conditions',
  '/cookie-policy': 'Cookie Policy',
  '/disclaimer': 'Disclaimer',
};

const SECTION_LABELS = {
  'services': 'Services',
  'locations': 'Locations',
  'loan-guides': 'Loan Guides',
  'resources': 'Resources',
  'blog': 'Blog',
  'admin': 'Admin',
};

const buildCrumbs = (pathname) => {
  if (pathname === '/') return [{ label: 'Home', path: '/' }];

  const crumbs = [{ label: 'Home', path: '/' }];
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length >= 1 && SECTION_LABELS[segments[0]]) {
    crumbs.push({
      label: SECTION_LABELS[segments[0]],
      path: segments[0] === 'resources' ? '/resources' : segments[0] === 'blog' ? '/blog' : segments[0] === 'admin' ? '/admin/blog' : null,
    });
  }

  const pageLabel = ROUTE_LABELS[pathname];
  if (pageLabel) {
    crumbs.push({ label: pageLabel, path: pathname, current: true });
  } else if (segments[0] === 'blog' && segments.length === 2) {
    crumbs.push({ label: segments[1].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), path: pathname, current: true });
  }

  return crumbs;
};

const Breadcrumbs = () => {
  const location = useLocation();
  const crumbs = buildCrumbs(location.pathname);

  useEffect(() => {
    if (crumbs.length < 2) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': crumbs.map((crumb, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'name': crumb.label,
        ...(crumb.path ? { 'item': `https://totalqualityinsurance.com${crumb.path}` } : {}),
      })),
    };

    const id = 'breadcrumb-schema-markup';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);

    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, [crumbs]);

  if (location.pathname === '/') return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="bg-gray-50 border-b border-gray-200"
      data-testid="breadcrumb-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ol className="flex items-center gap-1.5 py-3 text-sm">
          {crumbs.map((crumb, i) => (
            <li key={i} className="flex items-center gap-1.5">
              {i > 0 && (
                <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
              )}
              {crumb.current ? (
                <span
                  className="text-[#101C47] font-medium truncate max-w-[200px] sm:max-w-none"
                  aria-current="page"
                  data-testid={`breadcrumb-current`}
                >
                  {crumb.label}
                </span>
              ) : crumb.path ? (
                <Link
                  to={crumb.path}
                  className="text-gray-500 hover:text-[#f92626] transition-colors flex items-center gap-1"
                  data-testid={`breadcrumb-link-${i}`}
                >
                  {i === 0 && <Home size={14} className="flex-shrink-0" />}
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-gray-500">{crumb.label}</span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
